import { useState, useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AdjustmentList } from '@/components/adjustments/AdjustmentList'
import { AdjustmentForm } from '@/components/adjustments/AdjustmentForm'
import {
  useAdjustments,
  useCreateAdjustment,
  useUpdateAdjustment,
  useDeleteAdjustment,
} from '@/hooks/api/useAdjustments'
import { usePortfolio } from '@/hooks/api/usePortfolio'
import { useAssets } from '@/hooks/api/useAssets'
import type { AdjustmentScenario, PositionChange } from '@/types/api'

/**
 * Portfolio Adjustments Page
 * 
 * Features:
 * - Displays all saved adjustment scenarios
 * - Create new adjustments via form in side panel
 * - Edit existing adjustments
 * - Delete adjustments with confirmation
 * - Select up to 3 adjustments for scenario runs
 * - Selection persists in localStorage and URL params
 * - Links to scenarios page with selected adjustments
 */
function AdjustmentsPage() {
  // Data fetching
  const { data: adjustments = [], isLoading: isLoadingAdjustments } = useAdjustments()
  const { data: portfolio, isLoading: isLoadingPortfolio } = usePortfolio()
  const { data: assets = [], isLoading: isLoadingAssets } = useAssets()

  // Mutations
  const createMutation = useCreateAdjustment()
  const updateMutation = useUpdateAdjustment()
  const deleteMutation = useDeleteAdjustment()

  // UI State
  const [showForm, setShowForm] = useState(false)
  const [editingAdjustment, setEditingAdjustment] = useState<AdjustmentScenario | null>(null)
  
  // Selection state - persist to localStorage
  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedAdjustments')
      return saved ? JSON.parse(saved) : []
    }
    return []
  })

  // Persist selection to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedAdjustments', JSON.stringify(selectedIds))
    }
  }, [selectedIds])

  // Check for pre-selected adjustment from URL params (e.g., from scenarios page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const preselected = params.get('select')
    if (preselected && !selectedIds.includes(preselected)) {
      setSelectedIds((prev) => [...prev, preselected].slice(0, 3))
    }
  }, [])

  // Handlers
  const handleCreate = () => {
    setEditingAdjustment(null)
    setShowForm(true)
  }

  const handleEdit = (adjustment: AdjustmentScenario) => {
    setEditingAdjustment(adjustment)
    setShowForm(true)
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
    // Remove from selection if deleted
    setSelectedIds((prev) => prev.filter((sid) => sid !== id))
  }

  const handleToggleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      if (selected) {
        // Add if not already selected and under limit
        if (!prev.includes(id) && prev.length < 3) {
          return [...prev, id]
        }
        return prev
      } else {
        // Remove
        return prev.filter((sid) => sid !== id)
      }
    })
  }

  const handleSave = (data: {
    name: string
    description?: string
    changes: PositionChange[]
  }) => {
    if (editingAdjustment) {
      // Update existing
      updateMutation.mutate(
        {
          id: editingAdjustment.id,
          data: {
            name: data.name,
            description: data.description,
            changes: data.changes,
          },
        },
        {
          onSuccess: () => {
            setShowForm(false)
            setEditingAdjustment(null)
          },
        }
      )
    } else {
      // Create new
      createMutation.mutate(
        {
          name: data.name,
          description: data.description,
          changes: data.changes,
        },
        {
          onSuccess: () => {
            setShowForm(false)
            setEditingAdjustment(null)
          },
        }
      )
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingAdjustment(null)
  }

  // Loading state
  const isLoading = isLoadingAdjustments || isLoadingPortfolio || isLoadingAssets

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Portfolio Adjustments</h2>
          <p className="mt-1 text-gray-600">Loading...</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Error state if no portfolio
  if (!portfolio) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Portfolio Adjustments</h2>
          <p className="mt-1 text-gray-600">Create and manage portfolio adjustment scenarios.</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          Unable to load portfolio data. Please try refreshing the page.
        </div>
      </div>
    )
  }

  const selectionCount = selectedIds.length

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-900">Portfolio Adjustments</h2>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>
                    Adjustments model hypothetical portfolio changes for stress testing. Select up
                    to 3 to include in a scenario run.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="mt-1 text-gray-600">
              Create and manage portfolio adjustment scenarios for stress testing.
            </p>
          </div>
          <Button onClick={handleCreate}>Create Adjustment</Button>
        </div>

        {/* Selected Indicator */}
        {selectionCount > 0 && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg">
            <div className="flex items-center gap-2">
              <Badge variant="default">{selectionCount} selected</Badge>
              <span className="text-sm text-blue-800">
                {selectionCount === 1
                  ? '1 adjustment selected for next run'
                  : `${selectionCount} adjustments selected for next run`}
              </span>
            </div>
            <Link
              to="/scenarios"
              search={{ adjustments: selectedIds }}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Go to Scenarios →
            </Link>
          </div>
        )}

        {/* Empty State CTA (only when no adjustments exist) */}
        {adjustments.length === 0 && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mx-auto text-gray-400"
              >
                <path d="M12 2v20" />
                <path d="M2 12h20" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Create your first adjustment
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-4">
              Model portfolio changes like rebalancing, risk reduction, or sector rotation. Save
              scenarios to compare against your baseline portfolio in stress tests.
            </p>
            <Button onClick={handleCreate}>Create Adjustment</Button>
          </div>
        )}

        {/* Adjustment List */}
        {adjustments.length > 0 && (
          <AdjustmentList
            adjustments={adjustments}
            selectedIds={selectedIds}
            onSelect={handleToggleSelect}
            onEdit={handleEdit}
            onDelete={handleDelete}
            maxSelections={3}
          />
        )}

        {/* Form Sheet */}
        <Sheet open={showForm} onOpenChange={setShowForm}>
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader className="mb-4">
              <SheetTitle>
                {editingAdjustment ? 'Edit Adjustment' : 'Create Adjustment'}
              </SheetTitle>
              <SheetDescription>
                {editingAdjustment
                  ? 'Modify your portfolio adjustment scenario'
                  : 'Define a new portfolio adjustment scenario'}
              </SheetDescription>
            </SheetHeader>
            <AdjustmentForm
              adjustment={editingAdjustment}
              portfolio={portfolio}
              assets={assets}
              existingAdjustments={adjustments}
              onSubmit={handleSave}
              onCancel={handleCancel}
              isSubmitting={createMutation.isPending || updateMutation.isPending}
            />
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  )
}

export const Route = createFileRoute('/adjustments')({
  component: AdjustmentsPage,
})
